import { useEffect, useState } from 'react'

import { UrlParam, useUrlParams } from '@/lib/url/url-param.hook'

import type { BaseHorizontalTabsProps } from './base-horizontal'
import { getDefaultTabIndex } from './types'

type UseHorizontalTabsUrlArgs = {
  urlParamKey?: UrlParam
  items: BaseHorizontalTabsProps['items']
  /**
   * Explicit default tab. When provided it overrides the "first non-disabled
   * tab" heuristic, so the very first render selects the right tab (no flash).
   * The caller also owns persisting it to the URL — this component will not
   * auto-write a default in that case, avoiding a race with the caller.
   */
  defaultValue?: string
  shouldCheckUnsavedChanges?: boolean
  onChange?: (tab: string) => void
}

export const useHorizontalTabsUrl = ({
  urlParamKey,
  items,
  defaultValue,
  shouldCheckUnsavedChanges = false,
  onChange,
}: UseHorizontalTabsUrlArgs) => {
  const { urlParams, updateParam } = useUrlParams(shouldCheckUnsavedChanges)
  const [selectedValue, setSelectedValue] = useState<string | undefined>(undefined)

  const fallbackIndex = getDefaultTabIndex(items)
  const resolvedDefault = defaultValue ?? fallbackIndex
  const initialValue = urlParamKey ? urlParams[urlParamKey] || resolvedDefault : resolvedDefault
  const currentValue = (urlParamKey && urlParams[urlParamKey]) || selectedValue

  const handleValueChange = (newValue: string) => {
    setSelectedValue(newValue)
    if (urlParamKey) {
      updateParam(urlParamKey, newValue)
    }
    onChange?.(newValue)
  }

  useEffect(() => {
    // When the caller supplies an explicit defaultValue it owns persisting it
    // to the URL, so we skip the auto-write to avoid racing the caller (which
    // would briefly select this component's first tab before the caller's
    // default is applied).
    if (defaultValue !== undefined) {
      return
    }
    if (urlParamKey && !urlParams[urlParamKey] && fallbackIndex) {
      // oxlint-disable-next-line react/set-state-in-effect
      setSelectedValue(fallbackIndex)
      updateParam(urlParamKey, fallbackIndex, { replaceCurrentHistoryEntry: true })
    }
  }, [urlParamKey, fallbackIndex, defaultValue, urlParams, updateParam])

  return {
    value: currentValue,
    defaultValue: initialValue,
    onChange: handleValueChange,
  }
}
